import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/Button/Button';
import { UndoIcon, RedoIcon } from '@/components/ui/Icons';
import { useProjectUndoRedo, useProjectUndoRedoShortcuts } from '@/hooks/useProjectUndoRedo';
import './NavbarProjectUndoRedo.css';

const TOOLTIP_MAX_LEN = 220;

function truncatePreview(text: string): string {
  if (text.length <= TOOLTIP_MAX_LEN) return text;
  return `${text.slice(0, TOOLTIP_MAX_LEN - 1)}…`;
}

function buildUndoTooltip(preview: string | null, canUndo: boolean): string {
  if (!canUndo) return 'Нет изменений для отмены';
  if (preview) return `Отменить: ${truncatePreview(preview)}`;
  return 'Отменить последнее изменение (Ctrl+Z)';
}

function buildRedoTooltip(preview: string | null, canRedo: boolean): string {
  if (!canRedo) return 'Нет изменений для повтора';
  if (preview) return `Вернуть: ${truncatePreview(preview)}`;
  return 'Повторить отменённое изменение (Ctrl+Shift+Z)';
}

export function NavbarProjectUndoRedo() {
  const location = useLocation();
  const isProjectsRoute = location.pathname === '/' || location.pathname.startsWith('/projects');
  useProjectUndoRedoShortcuts({ enabled: isProjectsRoute });
  const {
    canUndo,
    canRedo,
    undoPreview,
    redoPreview,
    undo,
    redo,
    isUndoing,
    isRedoing,
  } = useProjectUndoRedo(null);

  const undoTooltip = useMemo(
    () => buildUndoTooltip(undoPreview, canUndo),
    [undoPreview, canUndo]
  );
  const redoTooltip = useMemo(
    () => buildRedoTooltip(redoPreview, canRedo),
    [redoPreview, canRedo]
  );

  if (!isProjectsRoute) return null;

  return (
    <div className="navbar-undo-redo" role="group" aria-label="Отмена и повтор изменений">
      <Button
        type="button"
        variant={canUndo && !isUndoing ? 'warning' : 'outlined'}
        size="s"
        className="navbar__header-icon-btn navbar-undo-redo__btn navbar-undo-redo__btn_undo"
        aria-label={undoTooltip}
        title={undoTooltip}
        disabled={!canUndo || isUndoing}
        onClick={() => undo()}
      >
        <UndoIcon size={18} aria-hidden />
      </Button>
      <Button
        type="button"
        variant={canRedo && !isRedoing ? 'primary' : 'outlined'}
        size="s"
        className="navbar__header-icon-btn navbar-undo-redo__btn navbar-undo-redo__btn_redo"
        aria-label={redoTooltip}
        title={redoTooltip}
        disabled={!canRedo || isRedoing}
        onClick={() => redo()}
      >
        <RedoIcon size={18} aria-hidden />
      </Button>
    </div>
  );
}
